import { useLayoutEffect, useRef } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import {
    useGridApiContext,
    type GridRenderEditCellParams
} from '../lib/dataGrid';
import {
    personName,
    personNames,
    personPickerProps,
    pickableRecent,
    usePersonPicker
} from './personPicker';
import { joinedToChips, chipsToJoined } from './formFields';
import { useRecentValueEntry } from './recentValueEntry';
import { useChipClipboard } from './chipClipboard';
import { employmentWindow } from '../features/users/employment';
import { toDayString } from '../utils/formatDate';
import TabSuggestAutocomplete from './TabSuggestAutocomplete';
import { takeThenHandOn } from './gridTabTakesSuggestion';

/** Borderless, filling the cell — as every other grid edit cell is styled. */
function cellInputSx(multiple: boolean) {
    return {
        height: '100%',
        '& .MuiInputBase-root': {
            height: '100%',
            fontSize: 'inherit',
            paddingLeft: '9px',
            // Chips + a text box in one row height: what does not fit scrolls
            // rather than pushing the row open. Same rule
            // GridMultiAutocompleteEditCell carries.
            ...(multiple ? { flexWrap: 'nowrap', overflowX: 'auto' } : {})
        },
        '& .MuiInputBase-root::before, & .MuiInputBase-root::after': {
            borderBottom: 'none !important'
        }
    };
}

/**
 * Grid edit cell for a person field — the picker of ../personPicker in a cell.
 *
 * The form's counterpart is formFields/personField; both offer the accounts
 * plus whatever this record already says, and neither accepts a typed name
 * unless the user holds `nameFreely` and picks the free-text entry.
 *
 * `multiple` serves the columns that hold several people. Those columns store
 * one ', '-joined string, so the cell hands the grid an array — the same shape
 * GridMultiAutocompleteEditCell commits.
 */
export default function GridPersonEditCell(
    props: GridRenderEditCellParams & {
        multiple?: boolean;
        /** A Grabungsleiter column: the marked accounts group first. */
        leads?: boolean;
        /** The last values of this field, newest first — the F2 entry. */
        recent?: readonly string[];
        /**
         * The ROW's date (an Aufnahmedatum, a work day): only who was
         * employed on it is offered (see users/employment). Each row asks
         * for its own day, so this is per cell, not per grid.
         */
        day?: unknown;
    }
) {
    const { id, field, value, hasFocus, multiple, leads, recent, day } = props;
    const { error } = props as GridRenderEditCellParams & {
        error?: string | null;
    };
    const apiRef = useGridApiContext();
    const inputRef = useRef<HTMLInputElement>(null);
    const picker = usePersonPicker(employmentWindow(toDayString(day)));

    useLayoutEffect(() => {
        if (hasFocus) {
            inputRef.current?.focus();
        }
    }, [hasFocus]);

    function commit(next: string) {
        apiRef.current.setEditCellValue({ id, field, value: next });
    }

    // Like GridMultiAutocompleteEditCell, a multi cell holds the ', '-joined
    // string the column displays; the column's `parse` makes the stored shape.
    const chips = joinedToChips(value, picker.knownNames);
    const single = `${value ?? ''}`;
    const multiPicker = personPickerProps(picker, {
        current: chips,
        multiple: true,
        leads
    });

    // "The one from a moment ago": the list above is alphabetical, this offers
    // the same field by recency (see ./recentValueEntry). No button in a cell —
    // see ValidatedGridEditCell.
    const { onKeyDown, menu } = useRecentValueEntry({
        // Only what could have been PICKED — the menu writes its entry
        // straight into the cell (see pickableRecent).
        recent: pickableRecent(picker, recent, multiple ? chips : single),
        value: single,
        onPick: commit
    });

    const renderInput = (params: Parameters<typeof TextField>[0]) => (
        <TextField
            {...params}
            inputRef={inputRef}
            variant="standard"
            error={!!error}
            sx={cellInputSx(Boolean(multiple))}
        />
    );

    const clipboard = useChipClipboard({
        value: chips,
        getLabel: (name: string) => name,
        options: multiPicker.options,
        fromText: picker.freeText ? (text) => text : undefined,
        onChange: (next) => commit(chipsToJoined(picker.distinct(next)))
    });

    return (
        <Tooltip
            open={!!error && hasFocus}
            title={error ?? ''}
            arrow
            placement="bottom"
        >
            <span style={{ display: 'flex', width: '100%' }}>
                {multiple ? (
                    <Autocomplete
                        {...multiPicker}
                        multiple
                        {...clipboard}
                        // No clear-all X inside an already narrow cell: the
                        // chips carry their own delete handles.
                        disableClearable
                        disableCloseOnSelect
                        fullWidth
                        size="small"
                        value={chips}
                        onChange={(_event, picked) =>
                            commit(
                                chipsToJoined(
                                    picker.distinct(
                                        personNames(picked).filter(Boolean)
                                    )
                                )
                            )
                        }
                        onKeyDown={onKeyDown}
                        sx={{ height: '100%' }}
                        renderInput={renderInput}
                    />
                ) : (
                    <TabSuggestAutocomplete
                        {...personPickerProps(picker, {
                            current: single,
                            leads
                        })}
                        onTake={(picked, event) =>
                            takeThenHandOn(
                                apiRef,
                                { id, field },
                                personName(picked),
                                event
                            )
                        }
                        fullWidth
                        value={single || null}
                        onChange={(_event, picked) =>
                            commit(personName(picked))
                        }
                        onKeyDown={onKeyDown}
                        sx={{ height: '100%' }}
                        renderInput={renderInput}
                    />
                )}
                {menu}
            </span>
        </Tooltip>
    );
}
